'use client'

import dynamic from 'next/dynamic'
import { useTranslations } from 'next-intl'

import { ModelsActionType } from '@/api/generated-api'
import { clearFanReturnPath } from '@/lib/auth/fanReturnPath'
import { useModalStore } from '@/stores/modalStore'

const AuthModal = dynamic(
  () => import('@/app/[locale]/components/Auth/AuthModal'),
  { ssr: false }
)
const ConfirmationModal = dynamic(
  () => import('@/app/[locale]/components/Auth/ConfirmationModal'),
  { ssr: false }
)
const ForgotPassword = dynamic(
  () => import('@/app/[locale]/components/Auth/ForgotPassword'),
  { ssr: false }
)
const UsernameModal = dynamic(
  () => import('@/app/[locale]/components/Auth/UsernameModal'),
  { ssr: false }
)
const RecoverPasswordModal = dynamic(
  () => import('@/app/[locale]/components/Auth/RecoverPasswordModal'),
  { ssr: false }
)
const PasswordResetSuccess = dynamic(
  () => import('@/app/[locale]/components/Auth/PasswordResetSuccess'),
  { ssr: false }
)

type AuthProviderDialogsProps = {
  onLogin: () => void
  onForgotPasswordSubmit: (email: string) => Promise<void>
  openModal: (
    modalType: 'auth' | 'confirmation' | 'forgot' | 'username' | 'recover',
    onUsernameSubmit?: (username: string) => void,
    onRecoverPasswordSubmit?: (password: string) => void
  ) => void
}

export function AuthProviderDialogs({
  onLogin,
  onForgotPasswordSubmit,
  openModal
}: AuthProviderDialogsProps) {
  const modalStore = useModalStore()
  const tCode = useTranslations('auth.confirmationCode')

  if (!modalStore.activeModal) {
    return null
  }

  return (
    <>
      <AuthModal
        open={modalStore.activeModal === 'auth'}
        onClose={() => {
          // US-ATH-14: a guest who gave up on signing in is not sent back to
          // the athlete profile by a later, unrelated sign-in.
          clearFanReturnPath()
          modalStore.closeAll()
          modalStore.setAuthModalInitialView('login')
          modalStore.setAuthModalSuccessType('signup')
        }}
        onForgotPassword={() => openModal('forgot')}
        onLogin={onLogin}
        initialView={modalStore.authModalInitialView}
        successType={modalStore.authModalSuccessType}
        referralEntityType={modalStore.referralEntityType ?? undefined}
        defaultEntityType={modalStore.signupDefaultEntityType ?? undefined}
      />
      <ConfirmationModal
        open={modalStore.activeModal === 'confirmation'}
        onClose={() => modalStore.closeAll()}
        onBack={() => {
          const entryPoint = modalStore.authModalEntryPoint
          const data = modalStore.confirmationData
          modalStore.closeAll()
          modalStore.setAuthModalEntryPoint(entryPoint)
          // US-ATH-01: back from the sign-up code lands on the form it came
          // from, with the role, the email and the consent still in place.
          if (
            data?.actionType === ModelsActionType.ActionTypeSignUp &&
            data.entityType
          ) {
            modalStore.setSignupDefaultEntityType(data.entityType)
            modalStore.setSignupPrefill({ email: data.email, consent: true })
            modalStore.setAuthModalInitialView('emailSignup')
          }
          modalStore.openModal('auth')
        }}
        onConfirm={async (code: string) => {
          try {
            modalStore.setConfirmationError('')
            await modalStore.confirmationHandlers?.onConfirm(code)
          } catch {
            // Flows that know the cause (sign-up, sign-in) set their own
            // message and do not throw; anything that reaches here is shown
            // as the plain wrong-code text rather than raw backend wording.
            modalStore.setConfirmationError(tCode('invalidCode'))
          }
        }}
        onResend={async () => {
          modalStore.setConfirmationError('')
          modalStore.setConfirmationHighlightResend(false)
          await modalStore.confirmationHandlers?.onResend()
        }}
        error={modalStore.confirmationError}
        highlightResend={modalStore.confirmationHighlightResend}
      />
      <ForgotPassword
        open={modalStore.activeModal === 'forgotPassword'}
        onClose={() => modalStore.closeAll()}
        onBack={() => openModal('auth')}
        onSubmit={onForgotPasswordSubmit}
      />
      <UsernameModal
        open={modalStore.activeModal === 'username'}
        onUsernameSubmit={modalStore.usernameSubmitHandler || undefined}
      />
      <RecoverPasswordModal
        open={modalStore.activeModal === 'recoverPassword'}
        onClose={() => modalStore.closeAll()}
        onCancel={() => {
          modalStore.setAuthModalInitialView('login')
          modalStore.openModal('auth')
        }}
        onSubmit={modalStore.recoverPasswordSubmitHandler ?? (() => {})}
      />
      <PasswordResetSuccess
        open={modalStore.activeModal === 'passwordResetSuccess'}
        onClose={() => modalStore.closeAll()}
        onContinue={() => {
          modalStore.setAuthModalInitialView('login')
          modalStore.openModal('auth')
        }}
      />
    </>
  )
}
